import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-29";
const ANSWER =
  "Ein Shopify Freelancer im deutschsprachigen Raum rechnet Anpassungen und Entwicklung meist nach Stunden ab. Bei Nicolai Schnepf sind es 110 € netto pro Stunde. Ein neuer Shop kostet als Festpreis ab etwa 3.000 € netto, Conversion-Optimierung ab 3.990 € netto. Der Preis hängt vom Umfang ab, nicht von einer Mindestlaufzeit.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/was-kostet-ein-shopify-freelancer";
const TITLE = "Was kostet ein Shopify Freelancer? Preise im Überblick";
const DESCRIPTION =
  "Stundensatz, Festpreise und Preistreiber: was ein Shopify Freelancer in Deutschland, Österreich und der Schweiz für Anpassungen, Shopaufbau und Optimierung kostet.";

const faqs = [
  {
    q: "Wie hoch ist der Stundensatz eines Shopify Freelancers?",
    a: "Bei mir 110 € netto pro Stunde für Theme-Anpassungen, Entwicklung und Schnittstellen. Abgerechnet wird nur die tatsächlich geleistete Zeit.",
  },
  {
    q: "Was kostet es, einen Shopify-Shop erstellen zu lassen?",
    a: "Ein kleiner Shop beginnt bei etwa 3.000 € netto, ein wachsender Shop liegt meist zwischen 4.000 und 10.000 € netto, ein Corporate-Shop ab 12.000 € netto.",
  },
  {
    q: "Was kostet Conversion-Optimierung für Shopify?",
    a: "Als Festpreis: 3.990 €, 9.900 € oder 18.900 € netto, je nach Umfang. Ob sich das lohnt, hängt von Traffic, Warenkorb und Marge ab und wird vorab geprüft.",
  },
  {
    q: "Was treibt den Preis nach oben?",
    a: "Viele Produktvarianten, eigene Funktionen, Schnittstellen zu Warenwirtschaft oder Buchhaltung, Datenübernahme aus einem alten Shop und mehrere Sprachen.",
  },
  {
    q: "Gibt es eine Mindestlaufzeit?",
    a: "Nein. Kleine Aufgaben sind genauso möglich wie größere Projekte.",
  },
];

export const Route = createFileRoute("/fragen/was-kostet-ein-shopify-freelancer")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Kosten",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Was kostet ein Shopify Freelancer?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Kosten"
      title="Was kostet ein Shopify Freelancer?"
      intro="Kurz gesagt: 110 € netto pro Stunde für Anpassungen, Festpreise für Shopaufbau und Conversion-Optimierung. Was Sie am Ende zahlen, hängt vom Umfang ab."
      source="frage-kosten-freelancer"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Preise auf einen Blick",
          p: [],
          list: [
            "Anpassungen und Entwicklung: 110 € netto pro Stunde",
            "Kleiner Shop: ab 3.000 € netto",
            "Wachsender Shop: 4.000 bis 10.000 € netto",
            "Corporate-Shop: ab 12.000 € netto",
            "Conversion-Optimierung: 3.990 €, 9.900 € oder 18.900 € netto",
          ],
        },
        {
          h: "Stunden oder Festpreis?",
          p: [
            "Kleine, klar umrissene Aufgaben rechnen sich nach Stunden am fairsten. Für einen neuen Shop oder ein Optimierungsprojekt ist ein Festpreis besser planbar, weil Umfang und Ergebnis vorab feststehen.",
          ],
        },
        {
          h: "Was den Preis beeinflusst",
          p: [],
          list: [
            "Anzahl der Produkte und Varianten",
            "Eigene Funktionen statt fertiger Apps",
            "Schnittstellen zu Warenwirtschaft, ERP oder DATEV",
            "Datenübernahme aus einem anderen Shopsystem",
            "Mehrere Sprachen oder Länder",
          ],
        },
        {
          h: "Freelancer oder Agentur beim Preis",
          p: [
            "Ein Freelancer spart Projektleitung und Abstimmungsrunden. Entscheidend ist nicht der Stundensatz allein, sondern wie viele Stunden tatsächlich im Shop landen.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
        { to: "/preise", label: "Preise" },
        { to: "/fragen/shopify-agentur-oder-freelancer", label: "Agentur oder Freelancer?" },
      ]}
    />
  );
}
